/**
 * Passage anchors: how a block id becomes a DOM element (design D4,
 * `designs/00-inbox/2026-07-30-passage-anchored-comments-ux-design.md` §12).
 *
 * `BlockIdentity.ts` mints a `blockId` attribute on every top-level block of a post's `source_json` and
 * renders NOTHING for it (`renderHTML: () => ({})`), so the ids are not in `html_content` and never will
 * be. The only join between an id and a rendered element is ORDER: the page-data response carries
 * `passageBlockIds`, one entry per top-level block in document order (the id, or `null` for a block
 * that carries none), and the rendered body's top-level children are in that same order. When the two
 * counts differ, the join is refused for the whole post: no anchors, no guessing, and the body is never
 * rewritten to make the counts agree.
 */

export type PassageBlockMap = Map<string, HTMLElement>;

export type PassageAnchorState = "ANCHORED" | "EDITED_SINCE" | "REMOVED";

/** What the markers and the thread need to know about one anchored thought; never the thought body. */
export type PassageAnchoredThoughtSummary = {
    publicId: string;
    blockId: string;
    state: PassageAnchorState;
    authorUsername?: string;
};

/**
 * Mirrors `ThoughtService.PASSAGE_ANCHOR_MIN_WORDS` (design D1). The server is the authority: this copy
 * exists only so a direct page load can decide whether fetching `passageBlockIds` is worth a request at
 * all (see `usePassageBlockIds`); a post the client thinks is long enough and the server does not simply
 * gets an empty list back.
 */
export const PASSAGE_ANCHOR_MIN_WORDS = 800;

/** `PublishThoughtRequest.anchorExcerpt` is `varchar(200)` on the server; the client clamps first. */
export const PASSAGE_ANCHOR_EXCERPT_MAX_CHARS = 200;

export function mapBlocksToDom(
    blockIds: ReadonlyArray<string | null | undefined> | null | undefined,
    bodyRoot: HTMLElement | null | undefined,
): PassageBlockMap | null {
    if (!blockIds || blockIds.length === 0 || !bodyRoot) {
        return null;
    }
    const children = Array.from(bodyRoot.children).filter(
        (node): node is HTMLElement => node instanceof HTMLElement,
    );
    if (children.length !== blockIds.length) {
        return null;
    }
    const map: PassageBlockMap = new Map();
    blockIds.forEach((blockId, index) => {
        const id = typeof blockId === "string" ? blockId.trim() : "";
        if (!id || map.has(id)) {
            return;
        }
        map.set(id, children[index]);
    });
    return map;
}

/** Same ids joined to the same elements: the identity check a re-derived map is compared by before it is stored. */
export function passageBlockMapsEqual(a: PassageBlockMap | null, b: PassageBlockMap | null): boolean {
    if (a === b) {
        return true;
    }
    if (!a || !b || a.size !== b.size) {
        return false;
    }
    for (const [blockId, element] of a) {
        if (b.get(blockId) !== element) {
            return false;
        }
    }
    return true;
}

/**
 * The block a DOM node (typically a selection's anchor) sits in, or null when it is outside every mapped
 * block: a null-id block, the title, the byline, another island.
 */
export function resolvePassageBlockForNode(
    map: PassageBlockMap | null | undefined,
    node: Node | null | undefined,
): { blockId: string; element: HTMLElement } | null {
    if (!map || !node) {
        return null;
    }
    for (const [blockId, element] of map) {
        if (element === node || element.contains(node)) {
            return { blockId, element };
        }
    }
    return null;
}

/**
 * The member's selection, in the shape the server verifies: whitespace collapsed (its own normalisation
 * before the substring check) and clamped to the column width on a word boundary, so a clamped prefix is
 * still a substring of the block's normalised text.
 */
export function buildPassageExcerpt(text: string | null | undefined): string {
    const normalised = (text ?? "").replace(/\s+/g, " ").trim();
    if (normalised.length <= PASSAGE_ANCHOR_EXCERPT_MAX_CHARS) {
        return normalised;
    }
    const head = normalised.slice(0, PASSAGE_ANCHOR_EXCERPT_MAX_CHARS);
    const lastSpace = head.lastIndexOf(" ");
    return (lastSpace > 0 ? head.slice(0, lastSpace) : head).trim();
}

export function countBodyWords(bodyRoot: HTMLElement | null | undefined): number {
    if (!bodyRoot) {
        return 0;
    }
    // Text nodes joined with a space rather than `textContent`, which glues "…end.</p><p>Start…" into
    // one word across every block boundary and under-counts by the paragraph count.
    const walker = bodyRoot.ownerDocument.createTreeWalker(bodyRoot, NodeFilter.SHOW_TEXT);
    const parts: string[] = [];
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const value = node.nodeValue?.trim();
        if (value) {
            parts.push(value);
        }
    }
    const text = parts.join(" ");
    return text ? text.split(/\s+/).length : 0;
}

export function parsePassageAnchorState(raw: unknown): PassageAnchorState | null {
    return raw === "ANCHORED" || raw === "EDITED_SINCE" || raw === "REMOVED" ? raw : null;
}
